import { useCallback, useEffect, useRef } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import uniq from 'lodash-es/uniq';
import { InfoIcon } from 'lucide-react';
import { useForm, useWatch, Controller, Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { Chips } from '@/components/atoms/Chip/Chip';
import FormFieldset from '@/components/atoms/Fieldset/templates/FormFieldset';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import useCapacityPolicy from '@/hooks/useCapacityPolicy';
import { API as desksApi, deskCache } from '@/hooks/useDesks';
import useMembers from '@/hooks/useMembers';
import { getPermissions } from '@/redux/permissions/selectors';
import {
  CAPACITY_DEFAULTS,
  CAPACITY_LIMITS,
  ConversationPriority,
  TIME_WINDOW_OPTIONS,
} from '@/shared/types/capacity-policy';
import { Desk } from '@/shared/types/desk';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { numberFieldProps } from '@/util/forms';
import { formatErrorMessage, isKeyEnter } from '@/util/util';
import { humanChatSchema } from '@/util/validation/schemas/dialog-schemas';

import AgentOverridesSection from './capacity/AgentOverridesSection';
import { OverrideDraft } from './capacity/types';

type Props = { desk?: Desk };

type HumanChatForm = {
  is_service_desk: boolean;
  tags: string[];
  tag: string;
  max_conversations: number;
  fair_distribution_limit: number;
  fair_distribution_window_seconds: number;
  conversation_priority: ConversationPriority;
  overrides: OverrideDraft[];
};

const HumanChatFieldset = ({ desk }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const desk_id = desk?.desk_id;

  const {
    policy,
    policyStatus,
    overrides,
    overridesStatus,
    savePolicy,
    deletePolicy,
    upsertOverride,
    deleteOverride,
    refresh,
  } = useCapacityPolicy(desk_id);
  const { members } = useMembers();

  // Baseline of overrides captured at hydrate time. The save-time diff compares the current form
  // drafts against THIS snapshot (form-vs-form), never against live server state — otherwise a
  // slow/late overrides refetch could be misread as "the user removed every override".
  const hydratedOverridesRef = useRef<OverrideDraft[]>([]);

  const canWrite = useSelector((state: RootState) => getPermissions(state, 'desks', actions.WRITE));

  const form = useForm<HumanChatForm>({
    mode: 'onChange',
    resolver: yupResolver(humanChatSchema) as Resolver<HumanChatForm>,
    defaultValues: { is_service_desk: false, tags: [], tag: '' },
  });

  const isServiceDesk = useWatch({ control: form.control, name: 'is_service_desk' });
  const tags = useWatch({ control: form.control, name: 'tags' });
  const watchedMaxConversations = useWatch({ control: form.control, name: 'max_conversations' });
  const watchedFairDistributionLimit = useWatch({
    control: form.control,
    name: 'fair_distribution_limit',
  });
  const { isDirty, isValid, errors } = form.formState;

  // With no persisted policy (`policy_id: null` — deleted on a prior disable, or the MOV-16784
  // endpoint isn't deployed on this env) pre-fill the UI defaults for EVERY field, ignoring the
  // orchestrator's desk-derived default numbers. That default echoes the legacy
  // `max_sessions_per_agent` dial, which survives a policy delete — showing it would make a
  // disabled → re-enabled desk look like its old config was never cleared.
  const isUnsavedPolicy = !policy?.policy_id;
  const defaultMaxConversations = isUnsavedPolicy
    ? CAPACITY_DEFAULTS.maxConversations
    : (policy?.max_conversations ?? CAPACITY_DEFAULTS.maxConversations);
  const defaultConversationPriority = isUnsavedPolicy
    ? ConversationPriority.LONGEST_WAITING
    : (policy?.conversation_priority ?? ConversationPriority.LONGEST_WAITING);
  const defaultFairDistributionLimit = isUnsavedPolicy
    ? CAPACITY_DEFAULTS.fairDistributionLimit
    : (policy?.fair_distribution_limit ?? CAPACITY_DEFAULTS.fairDistributionLimit);
  const defaultFairDistributionWindowSeconds = isUnsavedPolicy
    ? CAPACITY_DEFAULTS.fairDistributionWindowSeconds
    : (policy?.fair_distribution_window_seconds ?? CAPACITY_DEFAULTS.fairDistributionWindowSeconds);

  // Hydrate the form once both capacity queries have SETTLED (success or error — an error just
  // means "no policy yet", e.g. the endpoint isn't deployed). Waiting for both to settle is what
  // keeps the override diff safe: we never hydrate an empty override list while the real one is
  // still in flight. Never clobber a form the user is mid-edit on.
  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler */
  useEffect(() => {
    if (
      !desk ||
      policyStatus === 'pending' ||
      overridesStatus === 'pending' ||
      form.formState.isDirty
    ) {
      return;
    }
    // null override fields inherit the policy value (no "inherit" UI — show explicit numbers).
    const overrideDrafts = overrides.map((o) => ({
      user_id: o.user_id,
      max_conversations: o.max_conversations ?? defaultMaxConversations,
      fair_distribution_limit: o.fair_distribution_limit ?? defaultFairDistributionLimit,
    }));
    // Snapshot the hydrated overrides so the save-time diff compares form-vs-form.
    hydratedOverridesRef.current = overrideDrafts;
    form.reset({
      is_service_desk: desk.is_service_desk || false,
      tags: desk.metadata?.conversation_tags ?? [],
      tag: '',
      max_conversations: defaultMaxConversations,
      fair_distribution_limit: defaultFairDistributionLimit,
      fair_distribution_window_seconds: defaultFairDistributionWindowSeconds,
      conversation_priority: defaultConversationPriority,
      overrides: overrideDrafts,
    });
  }, [
    desk,
    policyStatus,
    overridesStatus,
    overrides,
    form,
    defaultMaxConversations,
    defaultConversationPriority,
    defaultFairDistributionLimit,
    defaultFairDistributionWindowSeconds,
  ]);
  /* eslint-enable react-you-might-not-need-an-effect/no-event-handler */

  const handleTagBlur = useCallback(() => {
    const tag = form.getValues('tag');
    if (tag && !form.getFieldState('tag').invalid) {
      form.clearErrors('tag');
      form.setValue('tags', [...form.getValues('tags'), tag], { shouldDirty: true });
      form.setValue('tag', '');
    }
  }, [form]);

  const handleTagKeyUp = useCallback(
    (e) => {
      if (isKeyEnter(e)) {
        handleTagBlur();
      }
    },
    [handleTagBlur]
  );

  const removeTag = (tag: string) => {
    form.setValue(
      'tags',
      form.getValues('tags').filter((item) => item !== tag),
      { shouldDirty: true }
    );
  };

  const onSave = form.handleSubmit(async (data) => {
    if (!desk_id || !desk) {
      return;
    }
    // Tracks whether the desk PUT already landed, so a later capacity failure reports a
    // partial save ("desk saved, capacity didn't") instead of a bare error.
    let deskSaved = false;
    try {
      // Desk slice — service-desk toggle + predefined tags in one partial PUT. Turning human
      // chat off clears the predefined tags too, so re-enabling starts from a clean slate.
      const updatedDesk = await desksApi.updateDesk({
        desk_id,
        is_service_desk: data.is_service_desk,
        metadata: { ...desk.metadata, conversation_tags: data.is_service_desk ? data.tags : [] },
      });
      deskCache.updated(queryClient, updatedDesk);
      deskSaved = true;

      if (data.is_service_desk) {
        // Capacity slice — only relevant for service desks (fields are hidden otherwise).
        // Capacity policy (POST when none exists yet, otherwise PUT) — must precede overrides.
        await savePolicy({
          name: desk.name.slice(0, 32),
          max_conversations: data.max_conversations,
          fair_distribution_limit: data.fair_distribution_limit,
          fair_distribution_window_seconds: data.fair_distribution_window_seconds,
          conversation_priority: data.conversation_priority,
          enabled: true,
        });

        // Diff current form drafts against the snapshot captured at hydrate time (form-vs-form),
        // so a late/refetched server list can't be misread as deletions. One PUT per changed
        // agent, one DELETE per removed.
        const original = hydratedOverridesRef.current;
        const drafts = data.overrides ?? [];
        const currentIds = new Set(drafts.map((o) => o.user_id));

        const upserts = drafts.filter((o) => {
          const prev = original.find((x) => x.user_id === o.user_id);
          return (
            !prev ||
            prev.max_conversations !== o.max_conversations ||
            prev.fair_distribution_limit !== o.fair_distribution_limit
          );
        });
        const deletions = original.filter((o) => !currentIds.has(o.user_id));

        await Promise.all([
          ...upserts.map((o) =>
            upsertOverride(o.user_id, {
              max_conversations: o.max_conversations,
              fair_distribution_limit: o.fair_distribution_limit,
            })
          ),
          ...deletions.map((o) => deleteOverride(o.user_id)),
        ]);
        // Re-baseline the diff to what was just saved — the refetch triggered by refresh()
        // may not settle before the user edits and saves again.
        hydratedOverridesRef.current = drafts;
        refresh();
        form.reset(data);
      } else {
        // Human chat disabled: remove the policy (cascades per-agent overrides server-side) so a
        // future re-enable starts from defaults, not the pre-disable config. deletePolicy also
        // purges the capacity query caches, so no refresh() here — invalidating would briefly
        // re-serve the pre-delete data and let the hydrate effect resurrect it. Reset the form to
        // defaults too — the capacity fields are unmounted, so React Hook Form would otherwise
        // retain their stale values and show them the moment the desk is re-enabled.
        await deletePolicy();
        hydratedOverridesRef.current = [];
        form.reset({
          is_service_desk: false,
          tags: [],
          tag: '',
          max_conversations: CAPACITY_DEFAULTS.maxConversations,
          fair_distribution_limit: CAPACITY_DEFAULTS.fairDistributionLimit,
          fair_distribution_window_seconds: CAPACITY_DEFAULTS.fairDistributionWindowSeconds,
          conversation_priority: ConversationPriority.LONGEST_WAITING,
          overrides: [],
        });
      }

      toast.success(t('environments.environment_updated', { 0: desk.name }));
    } catch (error) {
      const message = (error as Error)?.message;
      toast.error(
        deskSaved ? t('environments.human_chat_capacity_save_failed', { 0: message }) : message
      );
      refresh();
    }
  });

  return (
    <div className="mb-8">
      <FormFieldset
        title={t('environments.human_chat')}
        onSubmit={onSave}
        primaryButton={{ children: t('common.save'), disabled: !canWrite || !isDirty || !isValid }}
        isLoading={!desk || policyStatus === 'pending' || overridesStatus === 'pending'}
      >
        <div className="flex flex-col gap-4">
          {/* Chat toggle */}
          <Controller
            name="is_service_desk"
            control={form.control}
            render={({ field: { onChange, onBlur, value, name } }) => (
              <Switch
                variant="description"
                switchPosition="right"
                label={t('environments.chat')}
                description={t('environments.human_chat_subtitle')}
                checked={value}
                onCheckedChange={onChange}
                onBlur={onBlur}
                name={name}
                disabled={!desk || !canWrite}
              />
            )}
          />

          {isServiceDesk && (
            <>
              {/* Predefined tags */}
              <div>
                <Label htmlFor="tag" className="mb-2 block text-sm leading-5 font-medium">
                  {t('environments.predefined_tags')}
                </Label>
                <Controller
                  control={form.control}
                  name="tag"
                  render={({ field }) => (
                    <Input
                      id="tag"
                      placeholder={t('dialog.tags.type_a_tag')}
                      aria-invalid={!!errors.tag}
                      disabled={!canWrite}
                      {...field}
                      value={field.value ?? ''}
                      onBlur={() => {
                        field.onBlur();
                        handleTagBlur();
                      }}
                      onKeyUp={handleTagKeyUp}
                    />
                  )}
                />
                {errors.tag && (
                  <p className="mt-1 text-xs text-destructive">{formatErrorMessage(errors.tag)}</p>
                )}
                <p className="mt-2 text-sm leading-5 text-foreground-muted">
                  {t('environments.predefined_tags_subtitle')}
                </p>
                <Chips className="mt-2" chips={uniq(tags ?? [])} onDelete={removeTag} />
              </div>

              {/* Max conversations per agent */}
              <div className="max-w-80">
                <Label
                  htmlFor="max_conversations"
                  className="mb-2 block text-sm leading-5 font-medium"
                >
                  {t('environments.max_conversations_per_agent')}
                </Label>
                <Controller
                  control={form.control}
                  name="max_conversations"
                  render={({ field }) => (
                    <Input
                      id="max_conversations"
                      type="number"
                      min={CAPACITY_LIMITS.maxConversations.min}
                      max={CAPACITY_LIMITS.maxConversations.max}
                      disabled={!canWrite}
                      aria-invalid={!!errors.max_conversations}
                      {...numberFieldProps(field)}
                    />
                  )}
                />
                {errors.max_conversations && (
                  <p className="mt-1 text-xs text-destructive">
                    {errors.max_conversations.message}
                  </p>
                )}
                <p className="mt-2 text-sm leading-5 text-foreground-muted">
                  {t('environments.max_conversations_per_agent_subtitle')}
                </p>
              </div>

              {/* Fair distribution limit + Time window */}
              <div className="flex items-start gap-4">
                <div className="max-w-80 flex-1">
                  <Label
                    htmlFor="fair_distribution_limit"
                    className="mb-2 flex items-center gap-1 text-sm leading-5 font-medium"
                  >
                    {t('environments.fair_distribution_limit')}
                    <InfoTooltip
                      content={t('environments.fair_distribution_limit_tooltip')}
                      size="large"
                    >
                      <InfoIcon size={12} className="text-foreground-muted" />
                    </InfoTooltip>
                  </Label>
                  <Controller
                    control={form.control}
                    name="fair_distribution_limit"
                    render={({ field }) => (
                      <Input
                        id="fair_distribution_limit"
                        type="number"
                        min={CAPACITY_LIMITS.fairDistributionLimit.min}
                        max={CAPACITY_LIMITS.fairDistributionLimit.max}
                        disabled={!canWrite}
                        aria-invalid={!!errors.fair_distribution_limit}
                        {...numberFieldProps(field)}
                      />
                    )}
                  />
                  {errors.fair_distribution_limit && (
                    <p className="mt-1 text-xs text-destructive">
                      {errors.fair_distribution_limit.message}
                    </p>
                  )}
                  <p className="mt-2 text-sm leading-5 text-foreground-muted">
                    {t('environments.fair_distribution_limit_subtitle')}
                  </p>
                </div>
                <div className="w-48">
                  <Label className="mb-2 block text-sm leading-5 font-medium">
                    {t('environments.time_window')}
                  </Label>
                  <Controller
                    control={form.control}
                    name="fair_distribution_window_seconds"
                    render={({ field }) => (
                      <Select
                        value={field.value != null ? String(field.value) : undefined}
                        onValueChange={(v) => field.onChange(Number(v))}
                        disabled={!canWrite}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_WINDOW_OPTIONS.map((opt) => (
                            <SelectItem key={opt.seconds} value={String(opt.seconds)}>
                              {t(opt.labelKey)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              </div>

              {/* Conversation priority */}
              <div>
                <Label className="mb-2 flex items-center gap-1 text-sm leading-5 font-medium">
                  {t('environments.conversation_priority')}
                  <InfoTooltip
                    content={t('environments.conversation_priority_tooltip')}
                    size="large"
                  >
                    <InfoIcon size={12} className="text-foreground-muted" />
                  </InfoTooltip>
                </Label>
                <Controller
                  control={form.control}
                  name="conversation_priority"
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={!canWrite}
                      className="gap-2"
                    >
                      <RadioGroupItem
                        value={ConversationPriority.LONGEST_WAITING}
                        id="priority_longest"
                        description={t('environments.priority_longest_waiting_description')}
                      >
                        {t('environments.priority_longest_waiting')}
                      </RadioGroupItem>
                      <RadioGroupItem
                        value={ConversationPriority.EARLIEST_CREATED}
                        id="priority_earliest"
                        description={t('environments.priority_earliest_created_description')}
                      >
                        {t('environments.priority_earliest_created')}
                      </RadioGroupItem>
                    </RadioGroup>
                  )}
                />
              </div>

              {/* Agent overrides */}
              <Controller
                control={form.control}
                name="overrides"
                render={({ field }) => (
                  <AgentOverridesSection
                    value={field.value ?? []}
                    onChange={field.onChange}
                    members={members ?? []}
                    policyDefaults={{
                      // Use the live form values (not the loaded policy) so a new override
                      // defaults to what the admin is about to save, not the stale server state.
                      max_conversations:
                        watchedMaxConversations ?? CAPACITY_DEFAULTS.maxConversations,
                      fair_distribution_limit:
                        watchedFairDistributionLimit ?? CAPACITY_DEFAULTS.fairDistributionLimit,
                    }}
                    disabled={!canWrite}
                  />
                )}
              />
            </>
          )}
        </div>
      </FormFieldset>
    </div>
  );
};

export default HumanChatFieldset;
